import React, { useEffect, useRef, useState } from 'react';
import './LegalDocumentInputs.scss';
import {
    Button,
    CardBody,
    CardHeader,
    Col,
    FormGroup,
    InputGroup,
    Row,
} from 'reactstrap';
import ButtonLoader from '../../../../../components/Layout/Buttons/ButtonLoader';
import * as yup from 'yup';
import { Field, Form, Input } from '@availity/form';
import { MarketplaceBuyerLegalDocumentInputs } from '../../../../../slices/marketplace/buyer/models/buyer-legal-document-inputs';
import { useStateSelector } from '../../../../../store/selectors';
import createNotification from '../../../../../utils/createNotification';
import { GetBuyer } from '../../../../../slices/marketplace/buyer/thunks/get-buyer.thunk';
import { useDispatch } from 'react-redux';
import ReactInputMask from 'react-input-mask';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone } from '@fortawesome/free-solid-svg-icons';
import Helpers from '../../../../../utils/helper';
import { getAllStatesFlat } from '../../../../../utils/locationsHelper';
import { getCountries } from '../../../../../utils/constants';

export interface LegalDocumentInputsProps {
    setIsEditMode: (isEditMode: boolean) => void;
}
export const LegalDocumentInputs = (props: LegalDocumentInputsProps) => {
    const formRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);

    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch() as any;
    const legalDocumentInputs = useStateSelector(
        (state) => state.buyersState.targetBuyer?.profile?.legalDocumentInputs
    );
    const [contactPhone, setContactPhone] = useState<string>(
        Helpers.trimPhoneNumberFromMask(legalDocumentInputs?.contactPhone)
    );
    const [mainCorpPhone, setMainCorpPhone] = useState<string>(
        Helpers.trimPhoneNumberFromMask(legalDocumentInputs?.mainCorpPhone)
    );

    const [allStates, setAllStates] = useState<string[]>([]);

    useEffect(() => {
        axios
            .get('/api/Location/StatesByRegion')
            .then((response) => {
                if (response && response.data && response.data.data) {
                    const allStates = getAllStatesFlat(response.data.data);
                    setAllStates([...allStates]);
                }
            })
            .catch((error) => {
                createNotification(
                    'An error occured while fetching States',
                    'error'
                );
            });
    }, []);

    const renderLegalDocumentsInputs = () => {
        return (
            <div className="legal-documents-inputs-section">
                <CardHeader className="legal-document-inputs-header space-between">
                    Legal Documents Inputs
                    <div className="buttons-wrapper">
                        <ButtonLoader
                            buttonText={`Save`}
                            loaderButtonText={''}
                            disabled={false}
                            isLoading={isSaving}
                            onClick={() => {
                                formRef.current.handleSubmit();
                            }}
                            className={
                                'btn event-btn btn-primary upload-btn loader-submit'
                            }></ButtonLoader>{' '}
                        <Button
                            className="btn btn-primary"
                            onClick={() => {
                                props.setIsEditMode(false);
                            }}>
                            Back
                        </Button>
                    </div>
                </CardHeader>
                <div className="description-form">
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <label>Full Corporate Legal Name:</label>
                                <Field
                                    type="text"
                                    name="fullCorporateLegalName"
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <label>Type of Legal Entity:</label>
                                <Field type="text" name="typeOfLegalEntity" />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <label>
                                    Legal Entity State of Registration:
                                </label>
                                <Field
                                    type="text"
                                    name="legalEntityStateOfRegistration"
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <label>DBA Full Name:</label>
                                <Field type="text" name="dbaFullName" />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <label>DBA Nickname:</label>
                                <Field type="text" name="dbaNickName" />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <label>Main Corp Phone:</label>
                                <InputGroup
                                    className="mb-3"
                                    style={
                                        isMainCorpPhoneInvalid
                                            ? styles.invalidStyle
                                            : styles.validStyle
                                    }>
                                    <div className="input-group-text prepend-addon">
                                        <FontAwesomeIcon icon={faPhone} />
                                    </div>
                                    <ReactInputMask
                                        className="form-control"
                                        placeholder="(999) 999-9999"
                                        mask="(999) 999-9999"
                                        value={mainCorpPhone}
                                        maskChar={null}
                                        name="mainCorpPhone"
                                        id="txtPracticePhoneNumber"
                                        onChange={(event) => {
                                            if (event.target.value) {
                                                setIsMainCorpPhoneInvalid(
                                                    event.target.value &&
                                                        event.target.value.replace(
                                                            /[-() ]/g,
                                                            ''
                                                        ).length === 10
                                                        ? false
                                                        : true
                                                );
                                            } else {
                                                setIsMainCorpPhoneInvalid(
                                                    false
                                                );
                                            }
                                            setMainCorpPhone(
                                                event.target.value
                                            );
                                            formRef.current.setFieldValue(
                                                'mainCorpPhone',
                                                event.target.value?.replace(
                                                    /[-() ]/g,
                                                    ''
                                                )
                                            );
                                        }}
                                    />{' '}
                                </InputGroup>
                            </FormGroup>
                        </Col>
                    </Row>
                </div>
            </div>
        );
    };

    const renderMainCorpAddress = () => {
        return (
            <div className="legal-documents-inputs-section">
                <CardHeader className="legal-document-inputs-header">
                    {' '}
                    Main Corp Address
                </CardHeader>
                <div className="description-form">
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <label>Address Line 1:</label>
                                <Field type="text" name="addressLine1" />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <label>Address Line 2:</label>
                                <Field type="text" name="addressLine2" />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <label>City:</label>
                                <Field type="text" name="city" />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <label>State:</label>
                                <Input
                                    type="select"
                                    disabled={isSaving}
                                    name="state">
                                    <option value="" disabled>
                                        Select
                                    </option>
                                    {allStates.map((item) => (
                                        <option
                                            value={item}
                                            key={'state-' + item}>
                                            {item}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <label>Zip Code:</label>
                                <Field type="number" name="zipCode" />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <label>Select Country:</label>
                                <Input
                                    type="select"
                                    disabled={isSaving}
                                    name="country">
                                    {getCountries().map((item) => (
                                        <option
                                            value={item.id}
                                            key={'country ' + item.id}>
                                            {item.name}
                                        </option>
                                    ))}
                                </Input>{' '}
                            </FormGroup>
                        </Col>
                    </Row>
                </div>
            </div>
        );
    };
    const styles = {
        invalidStyle: {
            border: '1px solid red',
            borderRadius: '5px',
        },
        validStyle: {
            border: '',
        },
    };

    const [isContactPhoneInvalid, setIsContactPhoneInvalid] = useState(false);
    const [isMainCorpPhoneInvalid, setIsMainCorpPhoneInvalid] = useState(false);

    const validatePhoneNumber = (value: string) =>
        value && value.replace(/[-() ]/g, '').length === 10;

    const renderLegalNoticesContactInformation = () => {
        return (
            <div className="legal-documents-inputs-section">
                <CardHeader className="legal-document-inputs-header">
                    {' '}
                    Legal Notices Contact Information
                </CardHeader>
                <div className="description-form">
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <label>Name:</label>
                                <Field type="text" name="contactName" />
                            </FormGroup>
                        </Col>{' '}
                        <Col md={4}>
                            <FormGroup>
                                <label>Title:</label>
                                <Field type="text" name="contactTitle" />
                            </FormGroup>
                        </Col>
                    </Row>{' '}
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <label>Email:</label>
                                <Field type="text" name="contactEmail" />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <label>Phone:</label>
                                <InputGroup
                                    className="mb-3"
                                    style={
                                        isContactPhoneInvalid
                                            ? styles.invalidStyle
                                            : styles.validStyle
                                    }>
                                    <div className="input-group-text prepend-addon">
                                        <FontAwesomeIcon icon={faPhone} />
                                    </div>
                                    <ReactInputMask
                                        className="form-control"
                                        placeholder="(999) 999-9999"
                                        mask="(999) 999-9999"
                                        value={contactPhone}
                                        maskChar={null}
                                        name="contactPhone"
                                        id="txtPracticePhoneNumber"
                                        onChange={(event) => {
                                            if (event.target.value) {
                                                setIsContactPhoneInvalid(
                                                    event.target.value &&
                                                        event.target.value.replace(
                                                            /[-() ]/g,
                                                            ''
                                                        ).length === 10
                                                        ? false
                                                        : true
                                                );
                                            } else {
                                                setIsContactPhoneInvalid(false);
                                            }
                                            setContactPhone(event.target.value);
                                            formRef.current.setFieldValue(
                                                'contactPhone',
                                                event.target.value?.replace(
                                                    /[-() ]/g,
                                                    ''
                                                )
                                            );
                                        }}
                                    />{' '}
                                </InputGroup>
                            </FormGroup>
                        </Col>
                    </Row>
                </div>
            </div>
        );
    };

    const renderLegalIssuePreferences = () => {
        return (
            <div className="legal-documents-inputs-section">
                <CardHeader className="legal-document-inputs-header">
                    {' '}
                    Deal-Related Legal Issues Preferences
                </CardHeader>
                <div className="description-form">
                    <label>Under Construction</label>
                </div>
                <div className="mb-4"></div>
            </div>
        );
    };

    const updateProfile = async () => {
        return await dispatch(GetBuyer({ buyerId: null }));
    };

    const saveInputs = async (profile: MarketplaceBuyerLegalDocumentInputs) => {
        setIsSaving(true);
        axios
            .put(
                `/api/Marketplace/MarketplaceBuyerProfiles/LegalDocumentInputs`,
                profile
            )
            .catch(() => {
                createNotification(
                    'Something went wrong during saving profile',
                    'error'
                );
            })
            .finally(() => {
                updateProfile().finally(() => {
                    setIsSaving(false);
                });
            });
    };

    const renderForm = () => {
        return (
            <Form
                innerRef={formRef}
                validationSchema={yup.object().shape({
                    zipCode: yup
                        .string()
                        .matches(/^\d{5}(-\d{4})?$/, 'Invalid ZIP Code format'),
                    contactEmail: yup.string().email('Invalid email format'),
                })}
                initialValues={{
                    fullCorporateLegalName:
                        legalDocumentInputs?.fullCorporateLegalName ?? '',
                    typeOfLegalEntity:
                        legalDocumentInputs?.typeOfLegalEntity ?? '',
                    legalEntityStateOfRegistration:
                        legalDocumentInputs?.legalEntityStateOfRegistration ??
                        '',
                    dbaFullName: legalDocumentInputs?.dbaFullName ?? '',
                    dbaNickName: legalDocumentInputs?.dbaNickName ?? '',
                    mainCorpPhone: Helpers.trimPhoneNumberFromMask(
                        legalDocumentInputs?.mainCorpPhone ?? ''
                    ),
                    addressLine1: legalDocumentInputs?.addressLine1 ?? '',
                    addressLine2: legalDocumentInputs?.addressLine2 ?? '',
                    city: legalDocumentInputs?.city ?? '',
                    state: legalDocumentInputs?.state ?? '',
                    zipCode: legalDocumentInputs?.zipCode ?? '',
                    country: legalDocumentInputs?.country
                        ? legalDocumentInputs?.country
                        : 'US',
                    contactName: legalDocumentInputs?.contactName ?? '',
                    contactTitle: legalDocumentInputs?.contactTitle ?? '',
                    contactEmail: legalDocumentInputs?.contactEmail ?? '',
                    contactPhone: Helpers.trimPhoneNumberFromMask(
                        legalDocumentInputs?.contactPhone ?? ''
                    ),
                }}
                onSubmit={(values) => {
                    const isContactPhoneValid = validatePhoneNumber(
                        values.contactPhone
                    );
                    const isMainCorpPhoneValid = validatePhoneNumber(
                        values.mainCorpPhone
                    );
                    if (
                        (!isContactPhoneValid && values.contactPhone != '') ||
                        (!isMainCorpPhoneValid && values.mainCorpPhone != '')
                    ) {
                        return;
                    }
                    saveInputs(values as MarketplaceBuyerLegalDocumentInputs);
                }}>
                <CardBody>
                    {renderLegalDocumentsInputs()}
                    {renderMainCorpAddress()}
                    {renderLegalNoticesContactInformation()}
                    {renderLegalIssuePreferences()}
                </CardBody>
            </Form>
        );
    };

    return (
        <div className="legal-document-inputs">
            <CardBody>{renderForm()}</CardBody>
        </div>
    );
};
